import type { FieldDefinitionType } from '#components/dynamicForm/types/field.type';
import { useFiltersStore } from '#stores/filters.store';
import type { InputSearchModelType } from '@c4-links/vue3-links-components';
import { FilterDefinitionValueSearchType, type ProductTaxonomies, type StorePerimeters } from '@carrefour-gcs/bff-sdk';
import { useQuery } from '@tanstack/vue-query';
import { computed, ref, watch, type Ref } from 'vue';
import { useNotification } from '../notifications/useNotification';
import { useLanguage } from '../useLanguage';
import { FILTER_VALUE_PAGE_SIZE } from './constants/filterValuePageSize.constant';
import { requestFilterDefinitionValues } from './utils/filterDefinitionValueRequest.utils';

/**
 * Composable used for fetching the data of FilterDefinitionValue from BFF API
 */
export const useFilterDefinitionValue = (
  filterDefinition: FieldDefinitionType,
  debouncedSearch: Readonly<Ref<InputSearchModelType | undefined>>
) => {
  const { t } = useLanguage();
  const filterStore = useFiltersStore();
  const notification = useNotification();

  const pageSize = ref<number>(FILTER_VALUE_PAGE_SIZE);
  const searchFilterValue = ref<string[]>([]);
  const taxonomy = ref<ProductTaxonomies | null>(null);
  const storePerimeter = ref<StorePerimeters | null>(null);
  const searchType = ref<FilterDefinitionValueSearchType | undefined>(undefined);
  const paginationFilters = ref<{ page: number; pageSize: number } | undefined>(undefined);
  // True from "Show more" until the search changes: the values already displayed stay while the next ones load
  const isShowingMore = ref(false);
  const isQueryEnabled = computed(
    () =>
      !!taxonomy.value &&
      !!storePerimeter.value &&
      !!filterDefinition.id &&
      searchFilterValue.value.length > 0 &&
      searchFilterValue.value.some((val) => val.trim().length > 0)
  );

  // Watch filter store changes
  watch(
    () => [filterStore.filters] as const,
    async ([newFilters]) => {
      if (!newFilters.taxonomy || !newFilters.storePerimeter) {
        return;
      }

      taxonomy.value = newFilters.taxonomy as ProductTaxonomies;
      storePerimeter.value = newFilters.storePerimeter as StorePerimeters;
    },
    { deep: true, immediate: true }
  );

  // Watch debounced search changes and sync to refs
  watch(
    () => debouncedSearch.value,
    (newValue) => {
      // Update searchFilterValue
      if (!newValue?.searchValue || (Array.isArray(newValue.searchValue) && newValue.searchValue.length === 0)) {
        searchFilterValue.value = [];
        resetPagination();
      } else if (Array.isArray(newValue.searchValue)) {
        searchFilterValue.value = newValue.searchValue;
      } else {
        searchFilterValue.value = [newValue.searchValue];
      }

      // Update searchType
      searchType.value = newValue?.searchType as FilterDefinitionValueSearchType | undefined;
      isShowingMore.value = false;

      // Init paginationFilters if the field is paginated and searchFilterValue is not empty
      if (!paginationFilters.value && filterDefinition.isPaginated && searchFilterValue.value.length > 0) {
        paginationFilters.value = { page: 1, pageSize: pageSize.value };
      }
    },
    { deep: true }
  );

  const { data, isLoading, error, isError, isSuccess, isFetching } = useQuery({
    staleTime: 0,
    queryKey: computed(() => [
      'filterDefinitions',
      filterDefinition.id,
      taxonomy.value,
      storePerimeter.value,
      searchFilterValue.value,
      searchType.value,
      paginationFilters.value
    ]),
    queryFn: () =>
      requestFilterDefinitionValues({
        ...paginationFilters.value,
        taxonomy: taxonomy.value!,
        searchType: searchType.value,
        storePerimeter: storePerimeter.value!,
        filterDefinitionId: filterDefinition.id,
        searchFilterValue: searchFilterValue.value
      }),
    // Only "Show more" keeps the previous values: a new search must not display the results of the previous one
    placeholderData: (previousData) => (isShowingMore.value ? previousData : undefined),
    enabled: () => isQueryEnabled.value
  });

  const isFetchingMore = computed(() => isShowingMore.value && isFetching.value);

  watch(
    () => isError.value,
    (newIsError) => {
      if (newIsError) {
        notification.notify({
          status: 'error',
          title: t('commonLabel.errorData'),
          body: t('Please try again later or contact the administrator.')
        });

        console.error('Error fetching filter definitions:', error.value);
      }
    }
  );

  /**
   * Increment the pageSize and not the page number to fetch more data but keep the current page number for the next fetch
   */
  const showMore = async () => {
    if (!filterDefinition.isPaginated) {
      return;
    }

    isShowingMore.value = true;
    paginationFilters.value = {
      page: 1,
      pageSize: paginationFilters.value?.pageSize ? paginationFilters.value.pageSize + pageSize.value : pageSize.value
    };
  };

  const resetPagination = () => {
    if (!filterDefinition.isPaginated) {
      return;
    }

    pageSize.value = FILTER_VALUE_PAGE_SIZE;
    isShowingMore.value = false;
    paginationFilters.value = { page: 1, pageSize: pageSize.value };
  };

  /**
   * Fetches the values matching the given keys (used to display selected values missing from the first page).
   */
  const fetchValuesByKeys = async (keys: string[], { paginated = true }: { paginated?: boolean } = {}) => {
    if (!taxonomy.value || !storePerimeter.value || keys.length === 0) {
      return [];
    }

    // A tree returns one row per path: a page sized on the keys would truncate the subtrees of the selected nodes.
    // The total is useless here either way, so the count is skipped.
    const { data } = await requestFilterDefinitionValues({
      ...(paginated ? { page: 1, pageSize: keys.length } : {}),
      taxonomy: taxonomy.value,
      storePerimeter: storePerimeter.value,
      filterDefinitionId: filterDefinition.id,
      searchFilterValue: keys,
      searchType: FilterDefinitionValueSearchType.MULTISELECT,
      withCount: false
    });

    return data;
  };

  return {
    data,
    error,
    isLoading,
    isSuccess,
    isFetching,
    isFetchingMore,

    showMore,
    resetPagination,
    fetchValuesByKeys
  };
};
